<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";

import UiIcon from "@/components/UiIcon.vue";
import { formatNumber } from "@/utils/formatter";

/**
 * 내용만큼 늘어나는 글상자 — `minRows` 에서 시작해 `maxRows` 까지 자라고 그 뒤로는 안에서 스크롤한다.
 * - 부모가 준 attrs(id · aria-* · placeholder · disabled)는 textarea 로 간다. `FormField` 슬롯 안에 그대로 넣는다.
 * - `maxlength` 가 있으면 오른쪽 아래에 「12 / 200」 글자 수를 보인다. 남은 글자가 10% 이하면 warn 색이고, 그때만 화면 읽기에
 *   남은 글자를 알린다(타자마다 읽히지 않게).
 * - 크기 손잡이(resize)는 없다 — 높이는 내용이 정한다.
 * - 80차(UXS80-04 · UXB80-03 · UXC80-09): 기본 모양을 부품이 정한다 — 안쪽 여백 9px 12px(`sm` 6px 10px) · 14px(`sm` 13px) · 굵기 500 · 줄 높이 1.5 · 본문색.
 *   예전에는 아무것도 정하지 않아 브라우저 기본 2px 에 조상의 굵기(850)까지 상속했다. 화면이 `:deep(.ui-autogrow-input)` 로 덮으면 그쪽이 이긴다.
 * - 높이는 `rows` 속성과 무관하게 잰다: 잴 때만 `rows=1` 로 두고 내용 높이(`scrollHeight`)와 한 줄 높이를 읽는다. 줄 높이가 `normal` 같은 숫자 아닌 값이어도
 *   `rows=1` 상자의 높이로 알 수 있다(예전에는 `글자 크기 x 1.5` 로 어림해 `rows=2` 상자에 세 번째 줄 윗머리가 보였다). 숨은 상태(`display:none`)로 처음 그려진
 *   칸은 폭이 생길 때(`ResizeObserver`) 다시 잰다.
 * - 줄 상한을 넘치면(`overflowHint`, 기본 켬) 포커스가 없는 동안 상자 바닥에서 글을 부드럽게 끊고 오른쪽 아래에 ⌄ 표지를 둔다 — 세 번째 줄 윗머리가 반쯤 보이던
 *   「잘리다 만 모양」을 없애고 「더 있다」 를 알린다. 표지는 누르는 대상이 아니다: 칸을 누르면 포커스가 오고, 화면이 포커스 때 `maxRows` 를 키우면 펼쳐진다.
 *   루트에 `data-overflow="true"` 가 붙는다(화면 CSS 훅).
 * - 81차 `appearance="value"`(UXA81-10 · ux-a C8): 읽기 전용(조회 · 면접관)은 「쓰다 만 폼」이 아니라 값으로 읽혀야 한다. `readonly` 속성(포커스 · 복사 가능)을 달고 루트에 `is-readonly`,
 *   줄 수는 1 줄부터(`minRows` 무시 — 짧은 값에 빈 3줄 상자가 남지 않게), 값이 비면 `emptyText`(기본 「입력 없음」)를 옅은 글(placeholder 자리)로 낸다.
 *   **`readonly` 속성 단독은 모양을 바꾸지 않는다**(리뷰 R-DS-M1 — 저장 · 처리 중 잠금에 `:readonly="saving"` 을 써 온 15곳이 줄고 깜빡였다). 값 모양은 호출부가 `:appearance="canWrite ? 'input' : 'value'"` 로 고른다.
 *   칸 면 · 선은 전역 입력 층이 `!important` 로 입히므로 읽기 피부(면 `--ui-surface-2` · 선 없음)는 전역(`main.scss` — `.ui-autogrow.is-readonly`)이 입힌다.
 * - 82차 BL82-03: 81차 `variant="ghost"`(UXB81-16)는 호출 0 이라 걷었다 — 표 칸 안 고스트 입력은 화면 scoped(`JobAnalysisSkills.vue` `--ui-edit-fill`)가 맡는다. 전역 `is-ghost` 피부 · 부품 prop · 클래스 바인딩 삭제.
 * - 82차 `overflowAction`(Q4 · UXC82-04 ①): 문자열(「전체 보기」)을 주면 줄 상한을 넘친 칸의 ⌄ 표지가 **누를 수 있는 단추**(`button` · 24px 이상 · 접근 이름 = 그 글)가 된다. 누르면 칸에 포커스를 주고 `expand` 를 보낸다
 *   (화면이 포커스 때 `maxRows` 를 키워 펼친다 — 표지 · 칸 누름 · 이 단추가 같은 결과). 주지 않으면 지금처럼 장식 표지(`aria-hidden`).
 * - 92차 C92-20(UXC92-01): `overflowAction` 단추는 칸 **안쪽 아래에 얹지 않는다** — 390 에서 마지막 줄이 단추에 가려졌다. 칸 **아래 줄**(`.ui-autogrow-foot` — 글자 수 줄이 있으면 그 줄 안, 없으면 단추만의 줄) 오른쪽에 서고
 *   (글 + ⌄ · 누름 대상 24px 이상 · 접근 이름 = 그 글), 줄은 단추가 있을 때만 생긴다(넘치는 칸만 높이 +24 ~ 28, 글자 수 줄이 이미 있으면 0). 동작(포커스 + `expand`)은 그대로.
 * - `size="sm"` 은 표 칸 안 입력(`.is-sm` 훅 — 칸 면 · 선은 전역 입력 층이 입힌다).
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** 최대 글자 수(브라우저 입력 제한 + 글자 수 표시) */
    maxlength?: number;
    /** 처음(빈 칸) 높이의 줄 수 */
    minRows?: number;
    /** 이 줄 수를 넘으면 안에서 스크롤 */
    maxRows?: number;
    /** maxlength 가 있을 때 글자 수 표시 */
    showCount?: boolean;
    /** md = 14px · 안쪽 9px 12px(기본), sm = 13px · 6px 10px(표 칸 안) */
    size?: "md" | "sm";
    /** 줄 상한을 넘칠 때 바닥 끊김 + ⌄ 표지(포커스가 없는 동안) */
    overflowHint?: boolean;
    /** input = 입력 상자(기본) · value = 읽기 모드 — 값처럼 읽는 모양(`readonly` 속성 포함 · 1 줄부터 · 빈 값은 `emptyText`). 포커스 · 복사는 된다 */
    appearance?: "input" | "value";
    /** 읽기 전용이고 비었을 때 옅은 글 */
    emptyText?: string;
    /** 줄 상한을 넘친 칸의 ⌄ 표지를 누를 수 있는 단추로 — 그 글(「전체 보기」)이 이름 · 보이는 글. 주지 않으면 장식 표지 */
    overflowAction?: string;
  }>(),
  {
    maxlength: undefined,
    minRows: 3,
    maxRows: 12,
    showCount: true,
    size: "md",
    overflowHint: true,
    appearance: "input",
    emptyText: "입력 없음",
    overflowAction: undefined,
  },
);

const emit = defineEmits<{
  /** `overflowAction` 단추를 눌렀다 — 칸에는 이미 포커스를 줬다(화면이 `maxRows` 를 키운다) */
  expand: [];
}>();

const model = defineModel<string>({ default: "" });
/** 읽기 전용은 1 줄부터 — 높이는 내용이 정한다 */
const valueMode = computed(() => props.appearance === "value");
const minRowsApplied = computed(() => (valueMode.value ? 1 : props.minRows));
const attrs = useAttrs();
/** 값 모양은 `readonly` 속성 + 빈 값 안내를 입력에 얹는다 */
const inputAttrs = computed(() =>
  valueMode.value
    ? { ...attrs, readonly: true, placeholder: props.emptyText }
    : attrs,
);
const textarea = useTemplateRef<HTMLTextAreaElement>("textarea");

const length = computed(() => model.value.length);
const remaining = computed(() =>
  props.maxlength === undefined
    ? undefined
    : Math.max(0, props.maxlength - length.value),
);
/** 남은 글자가 이 수 이하면 경고 */
const warnAt = computed(() =>
  props.maxlength === undefined
    ? 0
    : Math.max(10, Math.round(props.maxlength * 0.1)),
);
const nearLimit = computed(
  () => remaining.value !== undefined && remaining.value <= warnAt.value,
);
const countVisible = computed(
  () => props.showCount && props.maxlength !== undefined,
);

/** 줄 상한을 넘쳐 내용이 더 있다 */
const overflowing = ref(false);
/** 포커스가 칸에 있다 — 있는 동안은 끊김 없이 안에서 스크롤한다 */
const focused = ref(false);
/** 상한을 넘쳤고 포커스가 없다 — 바닥을 줄 끝에서 끊고 ⌄ 표지를 둔다 */
const clipped = computed(
  () => props.overflowHint && overflowing.value && !focused.value,
);

/** 「전체 보기」 단추를 칸 아래 줄에 둔다 — 줄 상한을 넘쳐 끊긴 칸(포커스 없음)에서만 */
const actionShown = computed(() => clipped.value && !!props.overflowAction);

/** 지금 내용 높이에 맞춘다(줄 높이 × minRows ~ maxRows, 테두리 · 안쪽 여백 포함). */
function resize(): void {
  const el = textarea.value;
  if (!el) return;
  // 이전에 끊으려고 줄인 바닥 여백을 되돌려 놓고 잰다(이 아래 값은 모두 그 뒤 값 — 계산 스타일은 살아 있는 객체라 먼저 읽는다).
  el.style.paddingBottom = "";
  const style = getComputedStyle(el);
  const paddingTop = Number.parseFloat(style.paddingTop);
  const paddingBottom = Number.parseFloat(style.paddingBottom);
  const padding = paddingTop + paddingBottom;
  const border =
    Number.parseFloat(style.borderTopWidth) +
    Number.parseFloat(style.borderBottomWidth);
  const declaredLine = Number.parseFloat(style.lineHeight);
  const fontSize = Number.parseFloat(style.fontSize);
  // `rows` 와 현재 높이에 기대지 않게 한 줄 상자로 잰다 — 한 번 auto 로 줄여야 지운 뒤에도 scrollHeight 가 줄어든다.
  const rows = el.rows;
  el.rows = 1;
  el.style.height = "auto";
  const oneLineBox = el.clientHeight;
  const content = el.scrollHeight + border;
  el.rows = rows;
  // 숨은 칸(display:none)은 모든 값이 0 이다 — 그때는 그리지 않고 폭이 생길 때 다시 잰다.
  if (!oneLineBox) return;
  const lineHeight = declaredLine || Math.max(1, oneLineBox - padding);
  const frame = padding + border;
  const min = lineHeight * minRowsApplied.value + frame;
  const max =
    lineHeight * Math.max(minRowsApplied.value, props.maxRows) + frame;
  // scrollHeight 는 정수로 반올림되므로 1px 여유를 둔다.
  overflowing.value = content - max > 1;
  if (clipped.value) {
    // 바닥 안쪽 여백도 상자 안이라 넘친 글의 윗머리가 거기 비친다 — 여백을 줄이고 상자를 그만큼 줄여 줄 끝에서 끊는다.
    // 다음 줄 맨 위의 빈 줄 간격(글자 위 여유)만큼은 글이 안 보이므로 그만큼은 남긴다.
    const leading = Math.max(0, Math.floor((lineHeight - fontSize) / 2) - 1);
    const kept = Math.min(paddingBottom, leading);
    el.style.paddingBottom = `${kept}px`;
    el.style.height = `${max - paddingBottom + kept}px`;
  } else {
    el.style.height = `${Math.min(max, Math.max(min, content))}px`;
  }
  el.style.overflowY = overflowing.value ? "auto" : "hidden";
}

function onExpand(): void {
  textarea.value?.focus();
  emit("expand");
}

function onFocus(): void {
  focused.value = true;
  resize();
}

function onBlur(): void {
  focused.value = false;
  resize();
}

// 폭이 바뀌면(창 크기 · 사이드 패널) 줄바꿈이 달라지므로 다시 잰다. 높이 변화로는 다시 재지 않는다(무한 반복 방지).
let lastWidth = 0;
const observer =
  typeof ResizeObserver === "undefined"
    ? undefined
    : new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width !== lastWidth) {
          lastWidth = width;
          resize();
        }
      });

watch(model, () => void nextTick(resize));
watch(() => [minRowsApplied.value, props.maxRows, props.overflowHint], resize);

onMounted(() => {
  resize();
  if (textarea.value) observer?.observe(textarea.value);
});

onBeforeUnmount(() => observer?.disconnect());

defineExpose({
  /** 글상자에 포커스(FormField 제출 오류 시 첫 오류 칸으로) */
  focus: () => textarea.value?.focus(),
});
</script>

<template>
  <div
    class="ui-autogrow"
    :class="{
      'is-sm': size === 'sm',
      'is-readonly': valueMode,
    }"
    :data-overflow="overflowing ? 'true' : undefined"
  >
    <div class="ui-autogrow-field">
      <textarea
        ref="textarea"
        v-bind="inputAttrs"
        v-model="model"
        class="ui-autogrow-input"
        :maxlength="maxlength"
        :rows="minRowsApplied"
        @focus="onFocus"
        @blur="onBlur"
      />
      <span
        v-if="clipped && !overflowAction"
        class="ui-autogrow-more"
        aria-hidden="true"
        ><UiIcon name="chevron-down" :size="14"
      /></span>
    </div>
    <div
      v-if="actionShown || (countVisible && !valueMode)"
      class="ui-autogrow-foot"
    >
      <button
        v-if="actionShown"
        type="button"
        class="ui-autogrow-more-action"
        @click="onExpand"
      >
        <UiIcon name="chevron-down" :size="14" />{{ overflowAction }}
      </button>
      <template v-if="countVisible && !valueMode">
        <span
          class="ui-autogrow-count ui-num"
          :class="{ 'is-near': nearLimit, 'is-full': remaining === 0 }"
          aria-hidden="true"
          >{{ formatNumber(length) }} / {{ formatNumber(maxlength) }}</span
        >
        <span class="ui-sr-only" aria-live="polite">{{
          nearLimit ? `남은 글자 ${remaining}자` : ""
        }}</span>
      </template>
    </div>
  </div>
</template>

<style scoped>
.ui-autogrow {
  display: grid;
  gap: var(--ui-gap-2xs);
  min-width: 0;
}

.ui-autogrow-field {
  position: relative;
  min-width: 0;
}

/*
 * 프로토타입 `.field textarea{min-height:92px; resize:vertical}` 대신 높이는 resize() 가 정한다.
 * 기본 모양(80차 UXB80-03): 안쪽 여백 · 글자 크기 · 굵기 · 줄 높이를 부품이 정한다 — 조상의 굵기(850)를 물려받던 칸이 이제 500 이다.
 * 이 값들은 `!important` 가 아니라 화면 scoped `:deep()` 규칙이 덮을 수 있다.
 */
.ui-autogrow-input {
  box-sizing: border-box;
  display: block;
  width: 100%;
  min-height: 0;
  padding: 9px 12px;
  color: var(--ui-text);
  font-family: inherit;
  font-size: var(--ui-fs-md);
  font-weight: 500;
  line-height: 1.5;
  resize: none;
}

.ui-autogrow.is-sm .ui-autogrow-input {
  padding: 6px 10px;
  font-size: var(--ui-fs-sm);
}

/* 상한을 넘친 칸의 ⌄ 표지 — 누르는 대상이 아니다(칸을 누르면 포커스가 오고 화면이 펼친다). 글자 위에 얹히므로 칸 면 색 알약을 깐다. */
.ui-autogrow-more {
  position: absolute;
  right: 8px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text-3);
  box-shadow: 0 0 0 1px var(--ui-line);
  pointer-events: none;
}

/* 「전체 보기」 단추(`overflowAction`, 92차 C92-20) — 칸 아래 줄 오른쪽. 글 + ⌄ 의 낮은 보조 단추, 누름 대상 24px 이상(글자 수 줄 안에서는 글자 수 왼쪽). */
.ui-autogrow-more-action {
  display: inline-flex;
  gap: 2px;
  align-items: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-2);
  font: inherit;
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  line-height: 1;
  cursor: pointer;
}

.ui-autogrow-more-action:hover {
  background: var(--ui-surface-2);
  color: var(--ui-text);
}

.ui-autogrow-more-action:focus-visible {
  outline: 2px solid var(--ui-blue-600);
  outline-offset: 1px;
}

.ui-autogrow-foot {
  display: flex;
  gap: var(--ui-gap-sm);
  align-items: center;
  justify-content: flex-end;
}

.ui-autogrow-count {
  color: var(--ui-text-3);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  line-height: 1.4;
}

.ui-autogrow-count.is-near {
  color: var(--ui-warn);
  font-weight: 700;
}

.ui-autogrow-count.is-full {
  color: var(--ui-danger);
}
</style>
